// Frontend Permission Matrix - Mirrors backend permissions
// This file provides permission checking functions for frontend components

export const WASTE_GENERATOR_SITES_PAGE_ID = 'waste-generator-sites';
export const DISPOSAL_SITES_PAGE_ID = 'disposal-sites';
export const INVOICING_PAGE_ID = 'invoicing';

const PERMISSION_MATRIX = {
  // Module access definitions - maps pages to modules and roles
  modules: {
    compliance: {
      pages: ['compliance-records'],
      roles: ['globaladmin', 'companyadmin', 'compliancemanager', 'complianceuser']
    },
    verifications: {
      pages: ['audits', 'verification-questions', 'pre-audit-questionnaires'],
      roles: ['globaladmin', 'companyadmin', 'verificationmanager']
    },
    facilities: {
      pages: [
        'drainage-recordation', 
        'spcc-routine-inspection', 
        'spcc-annual-inspection', 
        'swppp-discharge-monitoring-inspection', 
        'swppp-quarterly-inspection',
        'response-equipment-inventory-inspection'
      ],
      roles: ['globaladmin', 'companyadmin', 'facilitymanager', 'facilityuser']
    },
    testing: {
      pages: [
        'qi-notification-exercise-log', 
        'annual-tabletop-exercise-log', 
        'transfer-hose-and-pipe-testing', 
        'environmental-sampling', 
        'environmental-lab-analysis'
      ],
      roles: ['globaladmin', 'companyadmin', 'facilitymanager', 'facilityuser', 'compliancemanager', 'complianceuser']
    },
    maintenance: {
      pages: [
        'hazardous-waste-removal', 
        'sump-pump-maintenance-inspection'
      ],
      roles: ['globaladmin', 'companyadmin', 'facilitymanager', 'facilityuser']
    },
    pws: {
      pages: [
        'weekly-flow-meter-logs', 
        'weekly-equipment-service-logs', 
        'weekly-pws-water-monitoring',
        'monthly-pws-water-sample', 
        'monthly-pws-analytical-report-review', 
        'quarterly-dlqor',
        'quarterly-colorimeter-calibration-verification', 
        'weekly-reclaimed-water-monitoring',
        'monthly-reclaimed-water-sample', 
        'monthly-reclaimed-analytical-report-review',
        'semi-annual-reclaimed-water-sample', 
        'semi-annual-reclaimed-analytical-report-review',
        'annual-reclaimed-water-sample', 
        'annual-reclaimed-analytical-report-review',
        'monthly-lab-summary'
      ],
      roles: ['globaladmin', 'companyadmin', 'pwsmanager', 'pwsuser']
    },
    annualAnalyticalDataSummary: {
      pages: ['annual-analytical-data-summary'],
      roles: ['globaladmin']
    },
    kenedyWaterReport: {
      pages: ['pws-report'],
      roles: ['globaladmin', 'pwsmanager']
    },
    r9: {
      pages: [
        'authorized-waste-receipt', 
        'analytical-result-form-for-awr', 
        'outgoing-solid-waste',
        'outgoing-produced-water-waste', 
        WASTE_GENERATOR_SITES_PAGE_ID,
        'billing-companies', 
        'waste-transporters', 
        DISPOSAL_SITES_PAGE_ID
      ],
      roles: ['globaladmin', 'companyadmin', 'r9admin', 'r9manager', 'r9user']
    },
    awrApprovers: {
      pages: ['awr-approvers'],
      roles: ['globaladmin', 'companyadmin', 'r9admin']
    },
    /** R9 quarterly TRRC PDF (admin/manager only; not r9user) */
    r9QuarterlyReport: {
      pages: ['r9-quarterly-report'],
      roles: ['globaladmin', 'companyadmin', 'r9admin', 'r9manager']
    },
    // Invoicing: accounting + admins; r9admin for operational invoice review tied to approved AWRs
    invoicing: {
      pages: [INVOICING_PAGE_ID],
      roles: ['globaladmin', 'companyadmin', 'accountingmanager', 'r9admin']
    },
    construction: {
      pages: [
        'project-permitting',
        'project-files',
      ],
      roles: ['globaladmin', 'companyadmin', 'constructionmanager', 'constructionuser']
    },
    engines: {
      pages: ['fields', 'wells', 'add-engine', 'add-engine-use', 'engine-running-hours'],
      roles: ['globaladmin', 'companyadmin', 'facilitymanager', 'complianceordermanager', 'complianceorderuser']
    },
    airPermitting: {
      pages: ['air-glycol-pump-log', 'air-opacity-monitoring', 'air-flare-log'],
      roles: ['globaladmin', 'companyadmin', 'facilitymanager', 'facilityuser']
    },
    airReference: {
      pages: ['air-glycol-pumps', 'air-flares'],
      roles: ['globaladmin', 'companyadmin']
    },
    conocoTasks: {
      pages: ['conoco-tasks', 'conoco-task-groups'],
      roles: ['globaladmin', 'companyadmin', 'conocotaskmanager', 'conocotaskuser']
    },
    sharepoint: {
      pages: ['sharepoint-documents'],
      roles: ['globaladmin']
    },
    sharepointAdmin: {
      pages: ['sharepoint-admin-resources'],
      roles: ['globaladmin']
    },
    templates: {
      pages: ['templates'],
      roles: ['globaladmin']
    },
    management: {
      pages: [
        'site-management',
        'pws-log-record-assignment',
        'company-management', 
        'manage-compliance-elements', 
        'manage-compliance-aspects', 
        'manage-record-types', 
        'manage-project-types',
        'manage-permit-agencies',
        'manage-project-permit-types',
        'manage-project-elements',
        'manage-approval-parameters', 
        'hazardous-waste-disposal-site-management',
        'waste-units-and-conversions'
      ],
      roles: ['globaladmin', 'companyadmin']
    },
    system: {
      pages: [
        'users',
        'user-access-matrix',
        'settings', 
        'profile'
      ],
      roles: ['globaladmin', 'companyadmin', 'r9admin']
    },
    adminDeveloper: {
      pages: ['pdf-testing', 'qa-lab', 'custom-form-builder', 'custom-form-entry', 'custom-report-builder'],
      roles: ['globaladmin']
    },
    reports: {
      pages: [
        'dashboard', 
        'compliance-map', 
        'compliance-summary-report'
      ],
      roles: ['globaladmin', 'companyadmin', 'r9admin', 'taskmanager', 'compliancemanager']
    },
    basic: {
      pages: [
        'home', 
        'calendar', 
        'library-of-records'
      ],
      roles: ['globaladmin', 'companyadmin', 'r9admin', 'r9manager', 'compliancemanager', 'facilitymanager', 'constructionmanager', 'pwsmanager', 'taskmanager', 'complianceuser', 'facilityuser', 'constructionuser', 'pwsuser', 'r9user']
    }
  },
  
  // Create permissions - which roles can create records in each module
  createPermissions: {
    compliance: ['globaladmin', 'companyadmin', 'compliancemanager'],
    facilities: ['globaladmin', 'companyadmin', 'facilitymanager'],
    testing: ['globaladmin', 'companyadmin', 'facilitymanager', 'compliancemanager'],
    maintenance: ['globaladmin', 'companyadmin', 'facilitymanager'],
    pws: ['globaladmin', 'companyadmin', 'pwsmanager'],
    r9: ['globaladmin', 'companyadmin', 'r9admin', 'r9manager', 'r9user'],
    r9QuarterlyReport: ['globaladmin', 'companyadmin', 'r9admin', 'r9manager'],
    construction: ['globaladmin', 'companyadmin', 'constructionmanager'],
    management: ['globaladmin', 'companyadmin'],
    system: ['globaladmin', 'companyadmin', 'r9admin'],
    reports: ['globaladmin', 'companyadmin', 'r9admin', 'taskmanager'],
    verifications: ['globaladmin', 'companyadmin', 'verificationmanager'],
    engines: ['globaladmin', 'companyadmin', 'facilitymanager'],
    airPermitting: ['globaladmin', 'companyadmin', 'facilitymanager', 'facilityuser'],
    airReference: ['globaladmin', 'companyadmin'],
    conocoTasks: ['globaladmin', 'companyadmin', 'conocotaskmanager'],
    sharepoint: ['globaladmin'],
    sharepointAdmin: ['globaladmin'],
    templates: ['globaladmin'],
    basic: ['globaladmin', 'companyadmin', 'r9admin', 'r9manager', 'compliancemanager', 'facilitymanager', 'constructionmanager', 'pwsmanager', 'taskmanager'],
    adminDeveloper: ['globaladmin']
  }
};

// Helper functions for working with the permission matrix

/**
 * Get user roles from user object
 * @param {Object} user - User object from auth context
 * @returns {Array} Array of role names
 */
const getUserRoles = (user) => {
  if (!user?.roles) return [];
  
  // Handle different possible role structures
  if (Array.isArray(user.roles)) {
    return user.roles.map(role => role.roleName || role);
  } else if (typeof user.roles === 'string') {
    return [user.roles];
  } else if (user.role) {
    return [user.role];
  } else if (user.roleName) {
    return [user.roleName];
  }
  
  return [];
};

/** Page ids for the named permission modules, in module order, without duplicates. */
export function pagesForModules(...moduleKeys) {
  const ids = [];
  moduleKeys.forEach((key) => {
    const pages = PERMISSION_MATRIX.modules[key]?.pages || [];
    pages.forEach((pageId) => {
      if (!ids.includes(pageId)) ids.push(pageId);
    });
  });
  return ids;
}

/**
 * Get all pages that a user with given roles can access
 * @param {Object} user - User object from auth context
 * @returns {Set} Set of page IDs the user can access
 */
export const getAccessiblePages = (user) => {
  const accessiblePages = new Set();
  const userRoles = getUserRoles(user);
  
  
  if (userRoles.length === 0) {
    return accessiblePages;
  }
  
  // Check each module
  Object.entries(PERMISSION_MATRIX.modules).forEach(([module, config]) => {
    const hasAccess = config.roles.some(role => userRoles.includes(role));
    if (hasAccess) {
      config.pages.forEach(page => accessiblePages.add(page));
    }
  });
  
  return accessiblePages;
};

const MODULES_WITHOUT_COMPANY_ASSIGNMENT = new Set(['basic', 'system']);
const ITM_FORM_TYPE_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 17];
const COMPANY_MODULE_SPECS = {
  r9: { codes: ['R9', 'R-9', 'R9MODULE'] },
  awrApprovers: { codes: ['R9', 'R-9', 'R9MODULE'] },
  r9QuarterlyReport: { codes: ['R9', 'R-9', 'R9MODULE'] },
  invoicing: { codes: ['R9', 'R-9', 'R9MODULE'] },
  pws: { codes: ['PWS'], includes: ['PWS'], formTypeIds: [18, 19] },
  kenedyWaterReport: { codes: ['PWS'], includes: ['PWS'], formTypeIds: [18, 19] },
  construction: {
    codes: ['CONSTRUCTION', 'CP', 'PROJECTS', 'CONSTRUCTION_PERMITTING'],
    includes: ['CONSTRUCTION'],
    formTypeIds: [15]
  },
  verifications: { includes: ['VERIF', 'AUDIT'] },
  conocoTasks: { codes: ['CONOCO_TASKS', 'CONOCOTASKS'] },
  engines: { codes: ['ENGINES', 'ENGINE'], formTypeIds: [16] },
  airPermitting: { codes: ['AIR_PERMITTING'] },
  airReference: { codes: ['AIR_PERMITTING'] },
  facilities: { codes: ['ITM'], formTypeIds: ITM_FORM_TYPE_IDS },
  testing: { codes: ['ITM'], formTypeIds: ITM_FORM_TYPE_IDS },
  maintenance: { codes: ['ITM'], formTypeIds: ITM_FORM_TYPE_IDS }
};

const normalizeModuleCodeValue = (entry) =>
  String(entry?.code ?? entry?.Code ?? entry ?? '')
    .toUpperCase()
    .trim()
    .replace(/\s+/g, '_');

const userHasCompanyAssignment = (user) =>
  Array.isArray(user?.companyAssignments) && user.companyAssignments.length > 0;

const assignedCompanyHasActiveModule = (user, module) => {
  const spec = COMPANY_MODULE_SPECS[module];
  if (!spec) return true;

  const codes = user?.activeModuleCodes ?? [];
  if (codes.some((entry) => {
    const code = normalizeModuleCodeValue(entry);
    if (!code) return false;
    if (spec.codes?.includes(code)) return true;
    if (spec.includes?.some((part) => code.includes(part))) return true;
    return false;
  })) {
    return true;
  }

  if (spec.formTypeIds?.length) {
    const modules = user?.activeModules ?? [];
    return modules.some((row) =>
      spec.formTypeIds.includes(Number(row?.FormTypeID ?? row?.formTypeID))
    );
  }

  return false;
};

/**
 * Check if a user can access a specific module
 * @param {Object} user - User object from auth context
 * @param {string} module - Module name to check
 * @returns {boolean} True if user can access the module
 */
export const canAccessModule = (user, module) => {
  const userRoles = getUserRoles(user).map((role) =>
    String(role || '')
      .toLowerCase()
      .replace(/\s+/g, '')
  );
  
  if (userRoles.length === 0) return false;
  if (userRoles.includes('globaladmin')) return true;

  if (!userHasCompanyAssignment(user) && !MODULES_WITHOUT_COMPANY_ASSIGNMENT.has(module)) {
    return false;
  }
  
  const moduleConfig = PERMISSION_MATRIX.modules[module];
  const hasRoleAccess = moduleConfig && moduleConfig.roles.some((role) =>
    userRoles.includes(
      String(role || '')
        .toLowerCase()
        .replace(/\s+/g, '')
    )
  );
  if (!hasRoleAccess) return false;

  return assignedCompanyHasActiveModule(user, module);
};

/**
 * Check if a user can create records in a specific module
 * @param {Object} user - User object from auth context
 * @param {string} module - Module name to check
 * @returns {boolean} True if user can create records in the module
 */
export const canCreateInModule = (user, module) => {
  const userRoles = getUserRoles(user);
  
  if (userRoles.length === 0) return false;
  
  const createRoles = PERMISSION_MATRIX.createPermissions[module];
  
  return createRoles && createRoles.some(role => userRoles.includes(role));
};

/**
 * Get the module that a page belongs to
 * @param {string} pageId - Page ID to look up
 * @returns {string} Module name or 'other' if not found
 */
export const getModuleForPage = (pageId) => {
  for (const [module, config] of Object.entries(PERMISSION_MATRIX.modules)) {
    if (config.pages.includes(pageId)) {
      return module;
    }
  }
  return 'other';
};

/**
 * Check if a user can access a specific page
 * @param {Object} user - User object from auth context
 * @param {string} pageId - Page ID to check
 * @returns {boolean} True if user can access the page
 */
export const canAccessPage = (user, pageId) => {
  const module = getModuleForPage(pageId);
  return canAccessModule(user, module);
};

/**
 * Filter menu items based on user permissions
 * @param {Array} menuItems - Array of menu items
 * @param {Object} user - User object from auth context
 * @returns {Array} Filtered menu items
 */
export const filterMenuByPermissions = (menuItems, user) => {
  if (!user) {
    return [];
  }
  
  const accessiblePages = getAccessiblePages(user);
  
  const filterItems = (items) => {
    return items.filter(item => {
      if (item.type === 'section') {
        item.children = filterItems(item.children);
        return item.children.length > 0;
      }
      
      // Check if item belongs to an accessible page
      const hasAccess = accessiblePages.has(item.id);
      return hasAccess;
    });
  };
  
  const result = filterItems(menuItems);
  return result;
};

export default {
  PERMISSION_MATRIX,
  getAccessiblePages,
  canAccessModule,
  canCreateInModule,
  getModuleForPage,
  canAccessPage,
  filterMenuByPermissions
}; 




